import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { LogOut, FileText, CheckCircle, MoreVertical, HelpCircle } from 'lucide-react';
import Button from '../ui/Button';
import { DEV_BYPASS_PAYMENT } from '../../config/dev';

interface BuilderHeaderProps {
    hasPaid: boolean;
    setHasPaid: (hasPaid: boolean) => void;
    isSaving: boolean;
    isDownloading?: boolean;
    showSaveSuccess: boolean;
    freeDownloadsRemaining?: number;
    /** Prix catalogue téléchargement (FCFA) */
    downloadPrice?: number;
    onSave: (showNotification: boolean) => Promise<string | null>;
    onOrder: (e: React.MouseEvent) => void;
    onDownload: () => void | Promise<void>;
    setShowHistoryModal: (show: boolean) => void;
    logout: () => void;
    onHelp?: () => void;
}

const BuilderHeader: React.FC<BuilderHeaderProps> = ({
    hasPaid,
    setHasPaid: _setHasPaid,
    isSaving,
    isDownloading = false,
    showSaveSuccess,
    freeDownloadsRemaining = 0,
    downloadPrice = 750,
    onSave,
    onOrder,
    onDownload,
    setShowHistoryModal,
    logout,
    onHelp,
}) => {
    const canDownload = DEV_BYPASS_PAYMENT || hasPaid;
    const busy = isSaving || isDownloading;
    const [menuOpen, setMenuOpen] = useState(false);
    const menuRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!menuOpen) return;
        const onPointerDown = (e: PointerEvent) => {
            if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
                setMenuOpen(false);
            }
        };
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setMenuOpen(false);
        };
        document.addEventListener('pointerdown', onPointerDown);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('pointerdown', onPointerDown);
            document.removeEventListener('keydown', onKey);
        };
    }, [menuOpen]);

    return (
        <header className="glass-morphism h-14 md:h-16 border-b border-slate-200 sticky top-0 z-30 px-3 sm:px-4 md:px-6 flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 md:gap-3 min-w-0" data-tour="logo">
                <Link
                    to="/"
                    className="p-1.5 bg-white border border-slate-200 rounded-lg shadow-sm hover:border-indigo-300 transition-colors shrink-0"
                >
                    <img src="/flashcv.png" alt="FlashCv Logo" className="w-7 h-7 md:w-8 md:h-8 object-contain" />
                </Link>
                <div className="min-w-0">
                    <h1 className="text-base md:text-lg font-bold text-slate-900 tracking-tight truncate">FlashCV</h1>
                    <p className="hidden sm:block text-[10px] text-slate-500 uppercase font-bold tracking-widest leading-none">
                        Edition en direct
                    </p>
                </div>
                {DEV_BYPASS_PAYMENT && (
                    <span className="hidden sm:inline-block ml-1 px-2 py-0.5 rounded text-[10px] font-black uppercase tracking-wider bg-amber-100 text-amber-800 border border-amber-200 shrink-0">
                        Mode dev
                    </span>
                )}
                {!DEV_BYPASS_PAYMENT && freeDownloadsRemaining > 0 && (
                    <span className="hidden md:inline-block ml-1 px-2 py-0.5 rounded text-[10px] font-black uppercase tracking-wider bg-indigo-100 text-indigo-800 border border-indigo-200 shrink-0">
                        {freeDownloadsRemaining} téléchargement{freeDownloadsRemaining > 1 ? 's' : ''} offert
                        {freeDownloadsRemaining > 1 ? 's' : ''}
                    </span>
                )}
            </div>

            <div className="flex items-center gap-1.5 sm:gap-2 md:gap-4 shrink-0" data-tour="actions">
                {onHelp && (
                    <Button
                        type="button"
                        variant="ghost"
                        data-tour="help"
                        className="h-8 md:h-9 px-2 sm:px-3 text-xs text-indigo-600 hover:bg-indigo-50 gap-1.5"
                        onClick={onHelp}
                        aria-label="Aide — relancer le guide"
                    >
                        <HelpCircle className="w-4 h-4" />
                        <span className="hidden sm:inline font-medium">Aide</span>
                    </Button>
                )}
                {canDownload ? (
                    <div className="flex items-center gap-1.5 sm:gap-2">
                        <Button
                            variant="outline"
                            data-tour="save"
                            className="h-8 md:h-9 px-2.5 sm:px-4 text-xs border-indigo-200 text-indigo-600 hover:bg-indigo-50"
                            onClick={() => onSave(true)}
                            disabled={busy}
                        >
                            <span className="sm:hidden">Sauver</span>
                            <span className="hidden sm:inline">Enregistrer</span>
                        </Button>
                        <Button
                            data-tour="order"
                            className={`h-8 md:h-9 px-2.5 sm:px-4 text-xs ${
                                DEV_BYPASS_PAYMENT ? 'bg-amber-600 hover:bg-amber-700' : 'bg-green-600 hover:bg-green-700'
                            }`}
                            onClick={() => {
                                void onDownload();
                            }}
                            disabled={busy}
                        >
                            <span className="sm:hidden">{isDownloading ? '…' : 'PDF'}</span>
                            <span className="hidden sm:inline">
                                {isDownloading
                                    ? 'Génération…'
                                    : DEV_BYPASS_PAYMENT
                                      ? 'Télécharger PDF (DEV)'
                                      : 'Télécharger PDF'}
                            </span>
                        </Button>
                    </div>
                ) : (
                    <div className="flex items-center gap-1.5 sm:gap-2">
                        <Button
                            variant="outline"
                            data-tour="save"
                            className="h-8 md:h-9 px-2.5 sm:px-4 text-xs border-indigo-200 text-indigo-600 hover:bg-indigo-50"
                            onClick={() => onSave(true)}
                            disabled={busy}
                        >
                            <span className="sm:hidden">Sauver</span>
                            <span className="hidden sm:inline">Enregistrer</span>
                        </Button>
                        <Button
                            type="button"
                            data-tour="order"
                            className="h-8 md:h-9 px-2.5 sm:px-4 text-xs"
                            onClick={onOrder}
                            disabled={busy}
                        >
                            <span className="sm:hidden">
                                {freeDownloadsRemaining > 0 ? 'Offert' : `${downloadPrice}`}
                            </span>
                            <span className="hidden sm:inline">
                                {freeDownloadsRemaining > 0
                                    ? 'Télécharger (offert)'
                                    : `Commander · ${downloadPrice} FCFA`}
                            </span>
                        </Button>
                    </div>
                )}

                {showSaveSuccess && (
                    <div className="hidden sm:flex items-center gap-2 px-3 py-1 bg-green-50 text-green-600 rounded-full border border-green-100 animate-slide-up">
                        <CheckCircle className="w-3 h-3" />
                        <span className="text-[10px] font-bold uppercase">Sauvegardé !</span>
                    </div>
                )}

                {/* Desktop secondary actions */}
                <div className="hidden md:flex items-center gap-2">
                    <div className="h-6 w-px bg-slate-200 mx-1" />
                    <Button
                        variant="ghost"
                        data-tour="mycvs"
                        className="h-9 px-3 text-indigo-600 hover:bg-indigo-50 gap-2"
                        onClick={() => setShowHistoryModal(true)}
                    >
                        <FileText className="w-4 h-4" />
                        <span className="font-medium">Mes CVs</span>
                    </Button>
                    <Button
                        variant="ghost"
                        data-tour="logout"
                        className="h-9 px-3 text-slate-500 hover:text-red-600 hover:bg-red-50 gap-2"
                        onClick={logout}
                    >
                        <LogOut className="w-4 h-4" />
                        <span className="font-medium">Déconnexion</span>
                    </Button>
                </div>

                {/* Mobile ⋮ menu — Mes CVs + Déconnexion sur petit écran */}
                <div className="relative md:hidden" ref={menuRef} data-tour="menu">
                    <button
                        type="button"
                        aria-label="Menu"
                        aria-expanded={menuOpen}
                        onClick={() => setMenuOpen((v) => !v)}
                        className="h-8 w-8 inline-flex items-center justify-center rounded-lg text-slate-600 hover:bg-slate-100"
                    >
                        <MoreVertical className="w-5 h-5" />
                    </button>
                    {menuOpen && (
                        <div className="absolute right-0 top-full mt-1 w-48 rounded-xl border border-slate-200 bg-white shadow-lg py-1 z-40">
                            <button
                                type="button"
                                className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-indigo-700 hover:bg-indigo-50"
                                onClick={() => {
                                    setMenuOpen(false);
                                    onHelp?.();
                                }}
                            >
                                <HelpCircle className="w-4 h-4" />
                                Aide / Guide
                            </button>
                            <button
                                type="button"
                                className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-indigo-700 hover:bg-indigo-50"
                                onClick={() => {
                                    setMenuOpen(false);
                                    setShowHistoryModal(true);
                                }}
                            >
                                <FileText className="w-4 h-4" />
                                Mes CVs
                            </button>
                            <button
                                type="button"
                                className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50"
                                onClick={() => {
                                    setMenuOpen(false);
                                    logout();
                                }}
                            >
                                <LogOut className="w-4 h-4" />
                                Déconnexion
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </header>
    );
};

export default BuilderHeader;
